<script setup lang="ts">
// NPM
import { ref, computed, reactive } from 'vue';
import { useMq } from 'vue3-mq';
import { useI18n } from 'vue-i18n';

// Components
import {
  BaseCard,
  BaseEmptyState,
  Modal,
  BaseButton,
  BaseBadge,
  FieldMultiFileUploader,
  FieldInput,
  FieldCheckbox,
  BaseAvatar,
  LinkedText,
} from '@magma-app/magma-lapilli';

// API
import { useUgcs } from '@/api/ugcs/use-ugcs';
import { useSubmitContent } from '@/api/ugcs/use-submit-content';
import { useSubmitPublication } from '@/api/ugcs/use-submit-publication';
import { useUgcFileUpload } from '@/composables/useUgcFileUpload';
import { useActionDeepLink } from '@/composables/useActionDeepLink';
import { useUserStore } from '@/stores/user';
import type { UserDashboardGetAllUgcsResponseType } from '@magma-app/magma-types/user-dashboard/ugc';
import FieldTextarea from '@/components/fields/FieldTextarea.vue';

// Plugins
const mq = useMq();
const { t } = useI18n({ useScope: 'global' });

type OngoingUgc = UserDashboardGetAllUgcsResponseType['ongoing'][number];
type OngoingBaseCardStatus = 'interested' | 'toValidate' | 'modificationRequested';
const MAX_UGC_FILE_SIZE = 300 * 1000 * 1000; // 300MB

const userStore = useUserStore();
const { data: ugcs, isLoading } = useUgcs();
const { mutateAsync: submitContent, isPending: isSubmittingContent } =
  useSubmitContent();
const { mutateAsync: submitPublication, isPending: isSubmittingPublication } =
  useSubmitPublication();

const normalizeUrlForSubmission = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }

  return /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;
};

const stripHttpProtocol = (value?: string | null) =>
  value?.replace(/^https?:\/\//i, '') || '';

const isValidSubmissionUrl = (value: string) => {
  const normalizedUrl = normalizeUrlForSubmission(value);
  if (!normalizedUrl) {
    return false;
  }

  try {
    const url = new URL(normalizedUrl);
    return ['http:', 'https:'].includes(url.protocol) && url.hostname.includes('.');
  } catch {
    return false;
  }
};

const isProofLinkValid = computed(() => {
  return isValidSubmissionUrl(proof.link);
});

const getBaseCardStatus = (
  status: OngoingUgc['status']
): OngoingBaseCardStatus | null => {
  if (
    status === 'interested'
    || status === 'toValidate'
    || status === 'modificationRequested'
  ) {
    return status;
  }

  return null;
};

const modal = ref<number | null>(null);
const currentModalStep = ref<'start' | 'add' | 'addLink' | 'modification' | 'end' | 'waiting'>('start');
const legalConsent = ref(false);
const selectedUgcId = ref(0);
const proofFiles = ref<File[]>([]);
const fileUpload = useUgcFileUpload(proofFiles, selectedUgcId);
const submissionError = ref<'content_failed' | 'publication_failed' | null>(null);
const proof = reactive({
  text: '',
  link: '',
});

const getUgcImgUrl = (ugc: { pictureUrl?: string | null }) =>
  ugc.pictureUrl
    ? ugc.pictureUrl
    : mq.lgPlus
      ? '/img/ugc_lg.png'
      : '/img/ugc.png';

const getModificationAdminName = (latestVersion: OngoingUgc['latestVersion']) =>
  [
    latestVersion?.modificationRequestedByAdminFirstname,
    latestVersion?.modificationRequestedByAdminLastname,
  ]
    .filter(Boolean)
    .join(' ');

const getModificationAdminInitials = (
  latestVersion: OngoingUgc['latestVersion']
) =>
  [
    latestVersion?.modificationRequestedByAdminFirstname?.charAt(0),
    latestVersion?.modificationRequestedByAdminLastname?.charAt(0),
  ]
    .filter(Boolean)
    .join('');

const updateProofLink = (value: string) => {
  proof.link = stripHttpProtocol(value);
  submissionError.value = null;
};

const resetForm = () => {
  proofFiles.value = [];
  proof.text = '';
  proof.link = '';
  legalConsent.value = false;
  selectedUgcId.value = 0;
  submissionError.value = null;
  fileUpload.clearAll();
};

const openUgcModal = (ugc: OngoingUgc) => {
  modal.value = ugc.ugcId;
  selectedUgcId.value = ugc.userUgcId || 0;

  if (ugc.status === 'interested') {
    currentModalStep.value = 'start';
  } else if (ugc.status === 'toValidate') {
    currentModalStep.value = 'waiting';
  } else if (ugc.status === 'modificationRequested') {
    currentModalStep.value = 'modification';
  } else {
    currentModalStep.value = 'start';
  }
};

const closeUgcModal = () => {
  if (fileUpload.isUploading.value) {
    const confirmed = window.confirm(
      t('ugcs.ongoing.add.closeConfirmation')
    );

    if (!confirmed) {
      return;
    }

    fileUpload.clearAll();
  }

  modal.value = null;
  currentModalStep.value = 'start';
  resetForm();
};

// Opens one UGC's modal directly from a ?ugcId= deep link.
useActionDeepLink<OngoingUgc>({
  type: 'ugc',
  bucket: 'ongoing',
  onOpen: (ugc) => openUgcModal(ugc),
});

const submitUnpublishedContent = async (ugc: OngoingUgc) => {
  submissionError.value = null;

  try {
    await submitContent({
      userUgcId: ugc.userUgcId || 0,
      textContent: ugc.needTextProof ? proof.text.trim() : undefined,
      linkUrl: ugc.needLinkProof
        ? normalizeUrlForSubmission(proof.link)
        : undefined,
      fileKeys: fileUpload.completedFiles.value.length
        ? fileUpload.completedFiles.value
        : undefined,
    });

    currentModalStep.value = 'end';
  } catch {
    submissionError.value = 'content_failed';
  }
};

const submitPublishedContent = async (ugc: OngoingUgc) => {
  submissionError.value = null;

  try {
    await submitPublication({
      userUgcId: ugc.userUgcId || 0,
      publicationLinkUrl: normalizeUrlForSubmission(proof.link),
    });

    currentModalStep.value = 'end';
  } catch {
    submissionError.value = 'publication_failed';
  }
};
</script>

<template>
  <div
    v-if="!isLoading"
    class="block h-full flex-1 px-4 md:px-8"
    :class="{
      'content-start lg:flex lg:flex-wrap': ugcs?.ongoing.length
    }"
  >
    <template v-if="ugcs?.ongoing.length">
      <Modal
        v-for="ugc in ugcs?.ongoing"
        :key="ugc.userUgcId"
        :open="modal === ugc.ugcId"
        content-class="!max-h-[calc(100dvh-2rem)] max-w-[90dvw] !overflow-y-auto overscroll-contain sm:max-w-[480px]"
        @open-focus="(e) => e.preventDefault()"
        @open-change="
          (open) => {
            if (open) {
              openUgcModal(ugc);
            }

            if (!open) {
              closeUgcModal();
            }
          }
        "
      >
        <template #trigger>
          <BaseCard
            aria-hidden="true"
            class="mb-2 lg:mb-6 lg:mr-6"
            :title="ugc.name"
            :img-url="getUgcImgUrl(ugc)"
            :points="ugc.points"
            
            :display-points="!ugc.pictureUrl"
            :badge-status="{
              points: ugc.points
            }"
            :status="getBaseCardStatus(ugc.status)"
          />
        </template>
        <template #content>
          <div 
            v-if="currentModalStep === 'start'"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-4">
              <img
                :src="getUgcImgUrl(ugc)"
                :alt="ugc.name"
                class="h-[200px] w-full rounded-[5px] object-cover"
              />
              <div class="flex flex-col gap-1">
                <h3
                  class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                >
                  {{ ugc.name }}
                </h3>
                <BaseBadge
                  size="md"
                  :text="ugc.points"
                  icon-type="icon"
                  icon-name="star"
                />
              </div>
              <div
                class="
                  rich-text-content max-h-[40dvh] min-w-0 break-words
                  overflow-y-auto text-mgm-txt-sm text-foreground-default
                  lg:max-h-none
                "
                v-html="ugc.description"
              ></div>
            </div>
            <BaseButton
              size="lg"
              class="w-full"
              @click="
                currentModalStep =
                  ugc.contentType === 'published' ? 'addLink' : 'add'
              "
              >{{
                ugc.contentType === 'published'
                  ? $t('ugcs.ongoing.start.ctaLink')
                  : $t('ugcs.ongoing.start.cta')
              }}</BaseButton
            >
          </div>
          <div 
            v-if="currentModalStep === 'modification'"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-4">
              <img
                :src="getUgcImgUrl(ugc)"
                :alt="ugc.name"
                class="h-[200px] w-full rounded-[5px] object-cover"
              />
              <div class="flex flex-col gap-1">
                <h3
                  class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                >
                  {{ $t('ugcs.ongoing.add.title') }}
                </h3>
                <p class="text-mgm-txt-sm text-foreground-default whitespace-pre-line">
                  <LinkedText :text="ugc.descriptionProof" />
                </p>
              </div>
              <div class="flex flex-col gap-4 border border-border-warning-emphasis rounded bg-background-warning-default p-4">
                <div class="flex flex-col gap-2">
                  <div class="flex flex-row justify-between">
                    <p class="text-mgm-txt-sm text-foreground-emphasis font-medium">
                      {{ $t('ugcs.ongoing.modifications.previousSubmission') }}
                    </p>
                    <p class="text-mgm-txt-sm text-foreground-warning-default font-medium">
                      {{ $t('ugcs.ongoing.modifications.required') }}
                    </p>
                  </div>

                  <div class="flex flex-col gap-4">
                    <div
                      v-if="ugc.latestVersion?.textContent"
                      class="flex flex-col gap-1"
                    >
                      <p class="text-mgm-txt-sm text-foreground-emphasis font-medium">
                        {{ $t('ugcs.ongoing.modifications.text') }}
                      </p>
                      <p class="whitespace-pre-wrap text-mgm-txt-sm text-foreground-default">
                        {{ ugc.latestVersion.textContent }}
                      </p>
                    </div>

                    <div
                      v-if="ugc.latestVersion?.linkUrl"
                      class="flex flex-col gap-1"
                    >
                      <p class="text-mgm-txt-sm text-foreground-emphasis font-medium">
                        {{ $t('ugcs.ongoing.modifications.link') }}
                      </p>
                      <a
                        :href="ugc.latestVersion.linkUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="break-all text-mgm-txt-sm font-medium text-foreground-brand-default underline"
                      >
                        {{ ugc.latestVersion.linkUrl }}
                      </a>
                    </div>

                    <FieldMultiFileUploader
                      v-if="ugc.latestVersion?.files.length"
                      :id="`previous-files-${ugc.userUgcId}`"
                      :label="$t('ugcs.ongoing.modifications.files')"
                      :readonly-files="ugc.latestVersion.files"
                      view-only
                    />

                    <p
                      v-if="
                        !ugc.latestVersion?.textContent &&
                        !ugc.latestVersion?.linkUrl &&
                        !ugc.latestVersion?.files.length
                      "
                      class="text-mgm-txt-sm text-foreground-default"
                    >
                      {{ $t('ugcs.ongoing.modifications.empty') }}
                    </p>
                  </div>
                </div>
                <div class="flex flex-col gap-2">
                  <div class="flex flex-col gap-1">
                    <p class="text-mgm-txt-sm text-foreground-emphasis font-medium">{{ $t('ugcs.ongoing.modifications.comment') }}</p>
                    <p class="text-mgm-txt-sm text-foreground-default">
                      {{ ugc.latestVersion?.modificationRequestText }}
                    </p>
                  </div>
                  <div class="flex flex-row gap-2 items-center">
                    <BaseAvatar
                      size="sm"
                      :src="ugc.latestVersion?.modificationRequestedByAdminProfilePictureUrl"
                      :alt="getModificationAdminName(ugc.latestVersion)"
                      :fallback="getModificationAdminInitials(ugc.latestVersion)"
                    />
                    <p class="text-foreground-emphasis font-medium text-mgm-txt-sm">{{ getModificationAdminName(ugc.latestVersion) }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-row gap-2">
              <BaseButton
                size="lg"
                class="flex-1"
                state="scdr"
                icon="chevron-left"
                icon-position="left"
                @click="currentModalStep = 'start'"
                >{{ $t('ugcs.ongoing.add.back') }}</BaseButton
              >
              <BaseButton
                size="lg"
                class="flex-1"
                @click="currentModalStep = 'add'"
                >{{ $t('ugcs.ongoing.modifications.cta') }}</BaseButton
              >
            </div>
          </div>
          <div 
            v-if="currentModalStep === 'add'"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-4"> 
              <div
                class="w-12 h-12 bg-[#F1F5F9] rounded-full border-8 border-[#F8FAFC] flex items-center justify-center"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="
                      M12 16V12M12 8H12.01
                      M22 12C22 17.5228 17.5228 22 12 22
                      C6.47715 22 2 17.5228 2 12
                      C2 6.47715 6.47715 2 12 2
                      C17.5228 2 22 6.47715 22 12Z
                    "
                    stroke="#5F6C85"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div class="flex flex-col gap-1">
                <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
                  {{ $t('ugcs.ongoing.add.title') }}
                </h3>
                <p class="text-mgm-txt-sm text-foreground-default whitespace-pre-line">
                  <LinkedText :text="ugc.descriptionProof" />
                </p>
              </div>
              <FieldTextarea
                v-if="ugc.needTextProof"
                v-model="proof.text"
                :label="$t('ugcs.ongoing.add.comment')"
              />
              <FieldMultiFileUploader
                v-if="ugc.needFileProof"
                :id="`file-${ugc.userUgcId}`"
                v-model="proofFiles"
                :label="$t('ugcs.ongoing.add.videos-photos')"
                :upload-states="fileUpload.uploadStates.value"
                :max-file-size="MAX_UGC_FILE_SIZE"
                required
                accept=".png,.jpg,.jpeg,.gif,.webp,.avif,.heic,.mp4,.mov,.webm,.mkv,.m4v"
                @file-remove="fileUpload.onFileRemoved"
                @file-rename="fileUpload.renameFile"
                @file-retry="fileUpload.retryFile"
              />
              <FieldInput
                v-if="ugc.needLinkProof"
                :model-value="proof.link"
                role="link"
                required
                :label="$t('ugcs.ongoing.add.link')"
                @update:model-value="updateProofLink"
              />
              <FieldCheckbox 
                id="consent"
                v-model="legalConsent"
                label
                required
              >{{
                $t('ugcs.ongoing.add.legal', {
                  organizationName: userStore.user?.organization?.name
                })
              }}</FieldCheckbox>
              <p
                v-if="submissionError === 'content_failed'"
                class="text-mgm-txt-xs text-foreground-danger-default"
              >
                {{ $t('ugcs.ongoing.add.submitError') }}
              </p>
            </div>

            <div class="flex gap-2">
              <BaseButton
                size="lg"
                class="flex-1"
                state="scdr"
                icon="chevron-left"
                icon-position="left"
                :disabled="isSubmittingContent || fileUpload.isUploading.value"
                @click="currentModalStep = 'start'"
                >{{ $t('ugcs.ongoing.add.back') }}</BaseButton
              >
              <BaseButton
                size="lg"
                class="flex-1"
                :loading="isSubmittingContent"
                :disabled="
                  !legalConsent ||
                  (ugc.needTextProof && proof.text.trim() === '') ||
                  (ugc.needFileProof && !fileUpload.allUploaded.value) ||
                  (ugc.needLinkProof && proof.link.trim() === '') ||
                  (ugc.needLinkProof && !isProofLinkValid) ||
                  isSubmittingContent ||
                  fileUpload.isUploading.value
                "
                @click="submitUnpublishedContent(ugc)"
                >{{ $t('ugcs.ongoing.add.cta') }}</BaseButton
              >
            </div>
          </div>
          <div 
            v-if="currentModalStep === 'addLink'"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-4"> 
              <div
                class="w-12 h-12 bg-[#F1F5F9] rounded-full border-8 border-[#F8FAFC] flex items-center justify-center"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="
                      M12 16V12M12 8H12.01
                      M22 12C22 17.5228 17.5228 22 12 22
                      C6.47715 22 2 17.5228 2 12
                      C2 6.47715 6.47715 2 12 2
                      C17.5228 2 22 6.47715 22 12Z
                    "
                    stroke="#5F6C85"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div class="flex flex-col gap-1">
                <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
                  {{ $t('ugcs.ongoing.addLink.title') }}
                </h3>
                <p class="text-mgm-txt-sm text-foreground-default">
                  {{ $t('ugcs.ongoing.addLink.description') }}
                </p>
              </div>
              <FieldInput
                :model-value="proof.link"
                role="link"
                required
                :label="$t('ugcs.ongoing.addLink.inputLabel')"
                @update:model-value="updateProofLink"
              />
              <p
                v-if="submissionError === 'publication_failed'"
                class="text-mgm-txt-xs text-foreground-danger-default"
              >
                {{ $t('ugcs.ongoing.addLink.submitError') }}
              </p>
            </div>

            <div class="flex gap-2">
              <BaseButton
                size="lg"
                class="flex-1"
                state="scdr"
                icon="chevron-left"
                icon-position="left"
                :disabled="isSubmittingPublication"
                @click="currentModalStep = 'start'"
                >{{ $t('ugcs.ongoing.add.back') }}</BaseButton
              >
              <BaseButton
                size="lg"
                class="flex-1"
                :loading="isSubmittingPublication"
                :disabled="
                  proof.link.trim() === '' ||
                  !isProofLinkValid ||
                  isSubmittingPublication
                "
                @click="submitPublishedContent(ugc)"
                >{{ $t('ugcs.ongoing.add.cta') }}</BaseButton
              >
            </div>
          </div>
          <div 
            v-if="currentModalStep === 'end'"
            class="flex flex-col gap-6"  
          >
            <div class="flex flex-col gap-4">
              <div
                class="w-12 h-12 bg-[#D1FADF] rounded-full border-8 border-[#ECFDF3] flex items-center justify-center"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="
                      M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818
                      C18.7182 19.709 16.9033 20.9725 14.8354 21.5839
                      C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746
                      C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371
                      C2.43727 15.628 1.87979 13.4881 2.02168 11.3363
                      C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706
                      C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013
                      C11.8996 1.7649 14.1003 1.98232 16.07 2.85999
                      M22 3.99999L12 14.01L9.00001 11.01
                    "
                    stroke="#03874C"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div class="flex flex-col gap-1">
                <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
                  {{ $t('ugcs.ongoing.end.title') }}
                </h3>
                <ul
                  class="list-disc pl-0 text-mgm-txt-sm text-foreground-default"
                >
                  <p>
                    {{
                      $t('ugcs.ongoing.end.content', {
                        email: ugc.contactAdminEmail
                      })
                    }}
                  </p>
                </ul>
              </div>
            </div>
            <BaseButton
              size="lg"
              class="w-full"
              @click="closeUgcModal"
              >{{ $t('generic.close') }}</BaseButton
            >
          </div>
          <div 
            v-if="currentModalStep === 'waiting'"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-4">
              <img
                :src="getUgcImgUrl(ugc)"
                :alt="ugc.name"
                class="h-[200px] w-full rounded-[5px] object-cover"
              />
              <div class="flex flex-col gap-1">
                <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
                  {{ ugc.name }}
                </h3>
                <BaseBadge
                  size="md"
                  :text="ugc.points"
                  icon-type="icon"
                  icon-name="star"
                />
              </div>
              <div
                class="
                  rich-text-content max-h-[40dvh] min-w-0 break-words
                  overflow-y-auto text-mgm-txt-sm text-foreground-default
                  lg:max-h-none
                "
                v-html="ugc.description"
              ></div>
            </div>
            <BaseButton
              size="lg"
              disabled
              class="!bg-background-warning-default w-full text-foreground-warning-default border-none"
              >{{ $t('ugcs.ongoing.waiting.cta') }}</BaseButton
            >
          </div>
        </template>
      </Modal>
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('ugcsView.empty.all')"
      type="empty-list"
    />
  </div>
</template>
